import React, { useEffect, useMemo, useState } from "react";
import { Select, Spin, Button } from "antd";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { useTranslation } from "react-i18next";
import { get } from "../../../utils/apiUtils";
import GoBackButtonArrow from "../../../assets/img/goBackArrow.png";

ChartJS.register(ArcElement, Tooltip, Legend);

const { Option } = Select;

type DocItem = {
  document_id: string;
  title?: string;
  document_name?: string;
};

type UsageItem = {
  code_id: string;
  name: string;
  description?: string;
  hash_color?: string;
  count_highlights: number;
  percentage_highlights: number;
};

const fallbackColors = [
  "#F4D9FF",
  "#A072EB",
  "#152C99",
  "#4A1C96",
  "#9747FF",
  "#B37FEB",
  "#D3ADF7",
  "#EFDBFF",
  "#F9F0FF",
  "#DADADA",
  "#8E44AD",
  "#3498DB",
  "#1ABC9C",
  "#F39C12",
  "#E74C3C",
];

const buildChartDataFromItems = (items: UsageItem[], limit: number) => {
  const sliced = items.slice(0, limit);

  const labels = sliced.map((c, i) => c.name || `Code ${i + 1}`);
  const values = sliced.map((c) => c.percentage_highlights ?? 0);

  const counts = sliced.map((c) => c.count_highlights ?? 0);
  const percents = sliced.map((c) =>
    Number((c.percentage_highlights ?? 0).toFixed(2))
  );

  const bgColors = sliced.map(
    (c, idx) => c.hash_color || fallbackColors[idx % fallbackColors.length]
  );

  const codeIds = sliced.map((c) => c.code_id);

  return {
    chartData: {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: bgColors,
          _counts: counts,
          _percents: percents,
          _codeIds: codeIds,
        },
      ],
    },
  };
};

const baseOptions: ChartOptions<"pie"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "right",
      labels: {
        usePointStyle: true,
        pointStyle: "rect",
        boxWidth: 12,
        padding: 20,
        color: "#707073",
        font: {
          size: 14,
          family: "Poppins",
          style: "normal",
          lineHeight: 1.4,
        },
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const idx = context.dataIndex;
          const label = context.label || "";
          const dataset: any = context.dataset;

          const counts: number[] = dataset._counts || [];
          const percents: number[] = dataset._percents || [];

          const count = counts[idx] ?? 0;
          const pct = percents[idx] ?? 0;

          return `${label}: ${count} highlights (${pct}%)`;
        },
      },
    },
  },
  layout: { padding: { right: 0 } },
};

type ViewMode = "main" | "subcodes";
type DocFilterValue = "all" | string;

const CodePieChart = ({ projectId }: any) => {
  const { t } = useTranslation("docProcessingCodes");
  const { t: tCommon } = useTranslation("common");
  const [limit, setLimit] = useState<number>(10);

  // Documents
  const [docsLoading, setDocsLoading] = useState(false);
  const [documents, setDocuments] = useState<DocItem[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<DocFilterValue>("all");

  // Drilldown
  const [viewMode, setViewMode] = useState<ViewMode>("main");
  const [selectedMainCode, setSelectedMainCode] = useState<{
    codeId: string;
    codeName: string;
  } | null>(null);

  // Pie data
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any>(null);

  const isAllDocs = selectedDocId === "all";

  const buildDocQuery = () => {
    // Swagger: document_ids OPTIONAL -> kur all docs, mos e dergo fare
    if (isAllDocs) return "";
    return `?document_ids=${encodeURIComponent(selectedDocId)}`;
  };

  const buildSubcodesQuery = () => {
    // ky endpoint e ka project_id required + document_ids optional
    const base = `?project_id=${encodeURIComponent(projectId)}`;
    if (isAllDocs) return base;
    return `${base}&document_ids=${encodeURIComponent(selectedDocId)}`;
  };

  // Fetch documents list
  useEffect(() => {
    if (!projectId) return;

    const fetchDocs = async () => {
      setDocsLoading(true);
      try {
        const res = await get<any>(
          `/documents?project_id=${projectId}&skip=0&limit=1000`
        );
        const items: DocItem[] = res.data?.items || [];
        setDocuments(items);

        // default gjithmon "All documents"
        setSelectedDocId("all");
      } catch (e) {
        console.error("Failed to fetch documents:", e);
        setDocuments([]);
        setSelectedDocId("all");
      } finally {
        setDocsLoading(false);
      }
    };

    fetchDocs();

    // reset state when project changes
    setViewMode("main");
    setSelectedMainCode(null);
    setChartData(null);
  }, [projectId]);

  // If document changes, reset to main view
  useEffect(() => {
    setViewMode("main");
    setSelectedMainCode(null);
  }, [selectedDocId]);

  // Fetch MAIN codes usage
  useEffect(() => {
    if (!projectId) return;
    if (viewMode !== "main") return;

    const fetchMainUsage = async () => {
      setLoading(true);
      try {
        const res = await get<any>(
          `/projects/${projectId}/codes/hierarchical-usage${buildDocQuery()}`
        );

        const items: UsageItem[] = res.data?.items || [];
        const built = buildChartDataFromItems(items, limit);
        setChartData(built.chartData);
      } catch (error) {
        console.error("Failed to fetch hierarchical usage:", error);
        setChartData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMainUsage();
  }, [projectId, selectedDocId, limit, viewMode]);

  // Fetch SUBCODES usage
  useEffect(() => {
    if (!projectId) return;
    if (viewMode !== "subcodes" || !selectedMainCode?.codeId) return;

    const fetchSubcodesUsage = async () => {
      setLoading(true);
      try {
        const res = await get<any>(
          `/codes/${
            selectedMainCode.codeId
          }/subcodes-usage${buildSubcodesQuery()}`
        );

        const items: UsageItem[] = res.data?.items || [];
        const built = buildChartDataFromItems(items, limit);
        setChartData(built.chartData);
      } catch (error) {
        console.error("Failed to fetch subcodes usage:", error);
        setChartData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSubcodesUsage();
  }, [projectId, selectedDocId, selectedMainCode, limit, viewMode]);

  const docOptions = useMemo(() => {
    const mapped = documents.map((d) => ({
      value: d.document_id,
      label: d.title || d.document_name || d.document_id,
    }));

    return [{ value: "all", label: tCommon("allDocuments") }, ...mapped];
  }, [documents, tCommon]);

  // Click handler (drilldown)
  const pieOptions: ChartOptions<"pie"> = useMemo(() => {
    return {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          ...baseOptions.plugins?.tooltip,
          callbacks: {
            label: (context) => {
              const idx = context.dataIndex;
              const label = context.label || "";
              const dataset: any = context.dataset;

              const counts: number[] = dataset._counts || [];
              const percents: number[] = dataset._percents || [];

              const count = counts[idx] ?? 0;
              const pct = percents[idx] ?? 0;

              return t("codePieChart.tooltipLabel", { label, count, pct });
            },
          },
        },
      },
      onClick: (_event, elements, chart) => {
        if (viewMode !== "main") return;
        if (!elements?.length) return;

        const el = elements[0];
        const idx = el.index;

        const dataset: any = chart.data.datasets?.[0];
        const codeIds: string[] = dataset?._codeIds || [];
        const labels: any[] = chart.data.labels || [];

        const clickedCodeId = codeIds[idx];
        const clickedName = String(labels[idx] ?? t("codePieChart.selectedCodeFallback"));

        if (!clickedCodeId) return;

        setSelectedMainCode({ codeId: clickedCodeId, codeName: clickedName });
        setViewMode("subcodes");
      },
    };
  }, [viewMode, t]);

  const title = useMemo(() => {
    if (viewMode === "subcodes" && selectedMainCode?.codeName) {
      return t("codePieChart.subcodesOf", { name: selectedMainCode.codeName });
    }
    return t("codePieChart.mainCodes");
  }, [viewMode, selectedMainCode, t]);

  return (
    <div className="card">
      {/* Controls row */}
      <div
        style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}
      >
        {/* Documents dropdown */}
        <div style={{ width: 320 }}>
          <Select
            value={selectedDocId}
            onChange={(val) => setSelectedDocId(val)}
            style={{ width: "100%", marginTop: "2rem" }}
            placeholder={
              docsLoading ? tCommon("loadingDocuments") : t("codePieChart.selectDocumentPlaceholder")
            }
            loading={docsLoading}
            showSearch
            optionFilterProp="children"
          >
            {docOptions.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>
        </div>

        {/* Top limit dropdown */}
        <div style={{ width: 180 }}>
          <Select
            value={limit}
            onChange={setLimit}
            style={{ width: "100%", marginTop: "2rem" }}
          >
            <Option value={10}>{tCommon("top10Codes")}</Option>
            <Option value={20}>{tCommon("top20Codes")}</Option>
          </Select>
        </div>
      </div>

      {/* Title + back */}
      <div style={{ maxWidth: 900, margin: "0 auto 12px", fontWeight: 600 }}>
        <h3
          style={{
            marginTop: "2rem",
            fontFamily: "Poppins",
            fontSize: "16px",
            fontWeight: 600,
            color: "#4A1C96",
          }}
        >
          {title}
        </h3>

        {viewMode === "subcodes" && (
          <div className="goBackToCodeButton">
            <Button
              onClick={() => {
                setViewMode("main");
                setSelectedMainCode(null);
              }}
              style={{
                height: "44px",
                backgroundColor: "white",
                border: "1px solid #4A1C96",
                fontSize: "14px",
                fontFamily: "Poppins",
                fontWeight: 500,
                color: "#4A1C96",
                borderRadius: "8px",
                marginTop: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <img
                src={GoBackButtonArrow}
                alt={tCommon("goBack")}
                style={{ width: 16, height: 16 }}
              />
              {tCommon("goBack")}
            </Button>
          </div>
        )}
      </div>

      {/* Chart */}
      <div
        className="graphContainer"
        style={{ maxWidth: 600, margin: "0 auto", height: 420 }}
      >
        {loading ? (
          <Spin />
        ) : (
          chartData && (
            <Pie
              key={`${viewMode}-${
                selectedMainCode?.codeId ?? "main"
              }-${selectedDocId}-${limit}`}
              data={chartData}
              options={pieOptions}
            />
          )
        )}
      </div>
    </div>
  );
};

export default CodePieChart;
